import { api } from 'redux/api/index.api';
import globalMessages from 'translations/messages/global-messages';
import { versionsState } from 'utils/constants';
import localStorageUser from 'utils/localStorageUser';

const getBodyForFormTemplateCreation = intl => {
  const language = localStorageUser.getLanguageUserFromLocalStorage();
  return {
    name: intl.formatMessage(globalMessages.form_name),
    form_languages: [
      {
        language: language || 'en',
        is_default: true,
      },
    ],
    form_template_parts: [
      {
        name: intl.formatMessage(globalMessages.part_name),
        order: 0,
        sections: [
          {
            name: intl.formatMessage(globalMessages.section_name),
            order: 0,
          },
        ],
      },
    ],
  };
};

const transformFormsTemplatesResponse = (response, arg) => {
  if (!arg?.withModulesPageFiltering) return response;

  const excludedFormIds = arg?.excludedFormIds || [];
  const filteredValues = (response?.values || [])
    .filter(form => form?.state === versionsState.publish)
    .filter(form => !excludedFormIds.includes(form?.id));
  const limit = arg?.limit || filteredValues.length;
  const offset = arg?.offset || 0;
  const values = filteredValues.slice(offset, offset + limit);

  return {
    ...response,
    values,
    count: filteredValues.length,
  };
};

export const formsLibraryServiceApi = api.injectEndpoints({
  endpoints: builder => ({
    fetchOrganizationFormTemplates: builder.query({
      query: ({ organizationId, searchValue, limit, offset, withModulesPageFiltering }) => ({
        url: `businessOrganization/${organizationId}/forms/filters?limit=${
          withModulesPageFiltering ? 1000 : limit
        }&offset=${withModulesPageFiltering ? 0 : offset}`,
        method: 'POST',
        body: { search_term: searchValue },
      }),
      transformResponse: (response, _meta, arg) => transformFormsTemplatesResponse(response, arg),
      providesTags: ['OrganizationFormTemplates'],
    }),
    createOrganizationFormTemplate: builder.mutation({
      query: ({ intl, organizationId }) => ({
        url: `businessOrganization/${organizationId}/forms`,
        method: 'POST',
        body: getBodyForFormTemplateCreation(intl),
      }),
      invalidatesTags: ['OrganizationFormTemplates', 'UnlinkedFormsByDynamicProperty'],
    }),
    deleteOrganizationFormTemplates: builder.mutation({
      query: ({ organizationId, formTemplateIds }) => ({
        url: `businessOrganization/${organizationId}/forms/bulk-delete`,
        method: 'POST',
        body: formTemplateIds,
      }),
      invalidatesTags: ['OrganizationFormTemplates'],
    }),
    duplicateOrganizationFormTemplate: builder.mutation({
      query: ({ organizationId, formTemplateId }) => ({
        url: `businessOrganization/${organizationId}/forms/${formTemplateId}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['OrganizationFormTemplates'],
    }),
    downloadOrganizationFormTemplates: builder.mutation({
      query: ({ organizationId, formTemplateIds }) => ({
        url: `businessOrganization/${organizationId}/forms/export`,
        method: 'POST',
        body: formTemplateIds,
      }),
    }),
    importOrganizationFormTemplates: builder.mutation({
      query: ({ organizationId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('display_name', file.name);
        formData.append('size', file.size);
        formData.append('mime_type', file.type);
        formData.append('file_name', file.name);
        return {
          url: `businessOrganization/${organizationId}/forms/import`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['OrganizationFormTemplates'],
    }),
    fetchFormsLibrary: builder.query({
      query: ({ search_term = '', limit, offset = 0, filters }) => ({
        url: `forms/filters`,
        method: 'POST',
        params: {
          limit,
          offset,
        },
        body: {
          search_term,
          form_visibility: filters?.form_visibility,
        },
      }),
      providesTags: ['FormsLibrary'],
    }),
    fetchFormsLibraryCount: builder.query({
      query: () => ({
        url: 'forms/numbers',
      }),
      providesTags: ['FormsLibraryCount'],
    }),
    deleteFormTemplate: builder.mutation({
      query: ({ id }) => ({
        url: `forms/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FormsLibrary', 'FormsLibraryCount'],
    }),
    createFormLibrary: builder.mutation({
      query: ({ formName, partName, sectionName, language }) => ({
        url: 'forms',
        method: 'POST',
        body: {
          name: formName,
          form_languages: [
            {
              language,
              is_default: true,
            },
          ],
          form_template_parts: [
            {
              name: partName,

              order: 0,
              sections: [
                {
                  name: sectionName,
                  order: 0,
                },
              ],
            },
          ],
        },
      }),
      invalidatesTags: ['FormsLibrary'],
    }),
    bulkUpdateFormsVisibility: builder.mutation({
      query: ({ ids, visibility }) => ({
        url: 'forms/bulk-visibility-update',
        method: 'PUT',
        body: {
          form_ids: ids,
          form_visibility: visibility,
        },
      }),
      invalidatesTags: ['FormsLibrary', 'FormsLibraryCount'],
    }),
    duplicateFormTemplate: builder.mutation({
      query: ({ id }) => ({
        url: `forms/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['FormsLibrary'],
    }),
    exportFormTemplates: builder.mutation({
      query: ({ ids }) => ({
        url: `forms/export`,
        method: 'POST',
        body: ids,
      }),
    }),
    importFormTemplate: builder.mutation({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('display_name', file.name);
        formData.append('size', file.size);
        formData.append('mime_type', file.type);
        formData.append('file_name', file.name);
        return {
          url: 'forms/import',
          method: 'POST',
          body: formData,
          headers: { 'x-skip-content-type': 'true' },
        };
      },
      invalidatesTags: ['FormsLibrary', 'FormsLibraryCount'],
    }),
    fetchReducedForms: builder.query({
      query: ({ formIds = [], searchTerm, offset = 0, limit = 1000 }) => ({
        url: `forms/reduced-forms`,
        method: 'POST',
        params: {
          offset,
          limit,
          ...(searchTerm ? { search_term: searchTerm } : {}),
        },
        body: {
          form_ids: formIds,
        },
      }),
      transformResponse: response => response?.forms || [],
      keepUnusedDataFor: 0,
    }),
    getSerializedForms: builder.query({
      query: ({ formIds }) => ({
        url: `forms/serialized`,
        method: 'POST',
        body: formIds,
      }),
      keepUnusedDataFor: 0,
    }),
    getLastTemplatesWithDetails: builder.query({
      queryFn: async (
        { projectId, moduleId, formInstanceIds },
        { dispatch: _dispatch, getState: _getState },
        _extraOptions,
        baseQuery,
      ) => {
        const lastTemplatesResult = await baseQuery({
          url: `projects/${projectId}/form-instances/get-last-templates`,
          method: 'POST',
          body: {
            module_id: moduleId,
            form_instance_ids: formInstanceIds ?? [],
          },
        });

        if (lastTemplatesResult.error) {
          return { error: lastTemplatesResult.error };
        }

        const forms = lastTemplatesResult.data?.forms || [];
        const familyIds = forms.map(form => form?.family_id).filter(Boolean);

        if (familyIds.length === 0) {
          return { data: { forms: [], serializedForms: [] } };
        }

        const serializedFormsResult = await baseQuery({
          url: `forms/serialized-from-family-ids`,
          method: 'POST',
          body: familyIds,
        });

        if (serializedFormsResult.error) {
          return { error: serializedFormsResult.error };
        }

        return {
          data: {
            forms: lastTemplatesResult.data?.forms || [],
            serializedForms: serializedFormsResult.data || [],
          },
        };
      },
      keepUnusedDataFor: 0,
    }),
    fetchProjectFormStatuses: builder.query({
      query: ({ formId }) => ({
        url: `forms/${formId}/statuses`,
        method: 'GET',
      }),
    }),
    fetchFormTemplate: builder.query({
      query: ({ formTemplateId }) => ({
        url: `formTemplates/${formTemplateId}/tree`,
        method: 'GET',
      }),
    }),
    fetchOrganizationDynamicProperties: builder.query({
      query: ({ organizationId, limit = 100, offset = 0 }) => ({
        url: `organizations/${organizationId}/dynamic-properties`,
        method: 'GET',
        params: {
          limit,
          offset,
        },
      }),
      providesTags: ['DynamicProperties'],
      keepUnusedDataFor: 0,
    }),
    fetchFormInstanceDynamicProperties: builder.query({
      query: ({ organizationId, organizationFormIds = [] }) => ({
        url: `organizations/${organizationId}/dynamic-properties`,
        method: 'POST',
        body: organizationFormIds,
      }),
      providesTags: ['DynamicProperties'],
      keepUnusedDataFor: 0,
    }),
    linkFormsToDynamicProperty: builder.mutation({
      query: ({ organizationId, dynamicPropertyId, formIds }) => ({
        url: `organizations/${organizationId}/dynamic-properties/${dynamicPropertyId}/link-forms`,
        method: 'POST',
        body: formIds,
      }),
      invalidatesTags: ['DynamicProperties', 'UnlinkedFormsByDynamicProperty'],
    }),
    unlinkFormsFromDynamicProperty: builder.mutation({
      query: ({ organizationId, dynamicPropertyId, formIds }) => ({
        url: `organizations/${organizationId}/dynamic-properties/${dynamicPropertyId}/unlink-forms`,
        method: 'POST',
        body: formIds,
      }),
      invalidatesTags: ['DynamicProperties', 'UnlinkedFormsByDynamicProperty'],
    }),
    createTenantDynamicProperty: builder.mutation({
      query: ({ organizationId, name }) => ({
        url: `organizations/${organizationId}/dynamic-properties/create-tenant`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['DynamicProperties'],
    }),
    fetchOrganizationUnlinkedFormsByDynamicProperty: builder.query({
      query: ({ organizationId, dynamicPropertyId, searchValue, limit, offset }) => ({
        url: `organizations/${organizationId}/dynamic-properties/${dynamicPropertyId}/unlinked-forms/filters`,
        method: 'POST',
        body: {
          search_term: searchValue,
          limit,
          offset,
        },
      }),
      providesTags: ['UnlinkedFormsByDynamicProperty'],
    }),
    createWorkspaceFormTemplate: builder.mutation({
      query: ({ intl, organizationId, workspaceId }) => ({
        url: `businessOrganization/${organizationId}/workspaces/${workspaceId}/forms`,
        method: 'POST',
        body: getBodyForFormTemplateCreation(intl),
      }),
      invalidatesTags: ['WorkspaceFormTemplates', 'UnlinkedFormsByDynamicProperty'],
    }),
    fetchWorkspaceFormTemplates: builder.query({
      query: ({
        organizationId,
        workspaceId,
        searchValue,
        limit,
        offset,
        withModulesPageFiltering,
      }) => ({
        url: `businessOrganization/${organizationId}/workspaces/${workspaceId}/forms/filters?limit=${
          withModulesPageFiltering ? 1000 : limit
        }&offset=${withModulesPageFiltering ? 0 : offset}`,
        method: 'POST',
        body: { search_term: searchValue },
      }),
      transformResponse: (response, _meta, arg) => transformFormsTemplatesResponse(response, arg),
      providesTags: ['WorkspaceFormTemplates'],
    }),
    deleteWorkspaceFormTemplates: builder.mutation({
      query: ({ organizationId, workspaceId, formTemplateIds }) => ({
        url: `businessOrganization/${organizationId}/workspaces/${workspaceId}/forms/bulk-delete`,
        method: 'POST',
        body: formTemplateIds,
      }),
      invalidatesTags: ['WorkspaceFormTemplates'],
    }),
    duplicateWorkspaceFormTemplate: builder.mutation({
      query: ({ organizationId, workspaceId, formTemplateId }) => ({
        url: `businessOrganization/${organizationId}/workspaces/${workspaceId}/forms/${formTemplateId}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['WorkspaceFormTemplates'],
    }),
    downloadWorkspaceFormTemplates: builder.mutation({
      query: ({ organizationId, workspaceId, formTemplateIds }) => ({
        url: `businessOrganization/${organizationId}/workspaces/${workspaceId}/forms/export`,
        method: 'POST',
        body: formTemplateIds,
      }),
    }),
    importWorkspaceFormTemplates: builder.mutation({
      query: ({ organizationId, workspaceId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('display_name', file.name);
        formData.append('size', file.size);
        formData.append('mime_type', file.type);
        formData.append('file_name', file.name);
        return {
          url: `businessOrganization/${organizationId}/workspaces/${workspaceId}/forms/import`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['WorkspaceFormTemplates'],
    }),
    fetchWorkspaceDynamicProperties: builder.query({
      query: ({ organizationId, workspaceId, limit = 100, offset = 0 }) => ({
        url: `organizations/${organizationId}/workspaces/${workspaceId}/dynamic-properties`,
        method: 'GET',
        params: {
          limit,
          offset,
        },
      }),
      providesTags: ['DynamicProperties'],
      keepUnusedDataFor: 0,
    }),
    linkFormsToWorkspaceDynamicProperty: builder.mutation({
      query: ({ organizationId, workspaceId, dynamicPropertyId, formIds }) => ({
        url: `organizations/${organizationId}/workspaces/${workspaceId}/dynamic-properties/${dynamicPropertyId}/link-forms`,
        method: 'POST',
        body: formIds,
      }),
      invalidatesTags: ['DynamicProperties', 'UnlinkedFormsByDynamicProperty'],
    }),
    unlinkFormsFromWorkspaceDynamicProperty: builder.mutation({
      query: ({ organizationId, workspaceId, dynamicPropertyId, formIds }) => ({
        url: `organizations/${organizationId}/workspaces/${workspaceId}/dynamic-properties/${dynamicPropertyId}/unlink-forms`,
        method: 'POST',
        body: formIds,
      }),
      invalidatesTags: ['DynamicProperties', 'UnlinkedFormsByDynamicProperty'],
    }),
    fetchWorkspaceUnlinkedFormsByDynamicProperty: builder.query({
      query: ({ organizationId, workspaceId, dynamicPropertyId, searchValue, limit, offset }) => ({
        url: `organizations/${organizationId}/workspaces/${workspaceId}/dynamic-properties/${dynamicPropertyId}/unlinked-forms/filters`,
        method: 'POST',
        body: {
          search_term: searchValue,
          limit,
          offset,
        },
      }),
      providesTags: ['UnlinkedFormsByDynamicProperty'],
    }),
    getLastTemplates: builder.query({
      query: ({ projectId, moduleId, formInstanceIds }) => ({
        url: `projects/${projectId}/form-instances/get-last-templates`,
        method: 'POST',
        body: {
          module_id: moduleId,
          form_instance_ids: formInstanceIds ?? [],
        },
      }),
      keepUnusedDataFor: 0,
    }),
    generateWorkloadOverviewReport: builder.mutation({
      query: ({ projectId, organizationId, timeZone, forms, language }) => ({
        url: `projects/${projectId}/report/workload-overview/generate`,
        method: 'POST',
        headers: {
          'Accept-Language': language || 'en',
        },
        body: {
          organization_id: organizationId,
          time_zone: timeZone,
          forms,
        },
      }),
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useFetchOrganizationFormTemplatesQuery,
  useCreateOrganizationFormTemplateMutation,
  useDeleteOrganizationFormTemplatesMutation,
  useDuplicateOrganizationFormTemplateMutation,
  useDownloadOrganizationFormTemplatesMutation,
  useImportOrganizationFormTemplatesMutation,
  useFetchFormsLibraryQuery,
  useFetchFormsLibraryCountQuery,
  useDeleteFormTemplateMutation,
  useCreateFormLibraryMutation,
  useBulkUpdateFormsVisibilityMutation,
  useDuplicateFormTemplateMutation,
  useExportFormTemplatesMutation,
  useImportFormTemplateMutation,
  useFetchReducedFormsQuery,
  useLazyFetchReducedFormsQuery,
  useGetSerializedFormsQuery,
  useLazyGetSerializedFormsQuery,
  useGetLastTemplatesWithDetailsQuery,
  useFetchProjectFormStatusesQuery,
  useLazyFetchProjectFormStatusesQuery,
  useLazyFetchFormTemplateQuery,
  useFetchFormTemplateQuery,
  useFetchOrganizationDynamicPropertiesQuery,
  useLazyFetchOrganizationDynamicPropertiesQuery,
  useFetchFormInstanceDynamicPropertiesQuery,
  useLazyFetchFormInstanceDynamicPropertiesQuery,
  useLinkFormsToDynamicPropertyMutation,
  useUnlinkFormsFromDynamicPropertyMutation,
  useCreateTenantDynamicPropertyMutation,
  useFetchOrganizationUnlinkedFormsByDynamicPropertyQuery,
  useCreateWorkspaceFormTemplateMutation,
  useFetchWorkspaceFormTemplatesQuery,
  useDeleteWorkspaceFormTemplatesMutation,
  useDuplicateWorkspaceFormTemplateMutation,
  useDownloadWorkspaceFormTemplatesMutation,
  useImportWorkspaceFormTemplatesMutation,
  useFetchWorkspaceDynamicPropertiesQuery,
  useLazyFetchWorkspaceDynamicPropertiesQuery,
  useLinkFormsToWorkspaceDynamicPropertyMutation,
  useUnlinkFormsFromWorkspaceDynamicPropertyMutation,
  useFetchWorkspaceUnlinkedFormsByDynamicPropertyQuery,
  useGetLastTemplatesQuery,
  useLazyGetLastTemplatesQuery,
  useGenerateWorkloadOverviewReportMutation,
} = formsLibraryServiceApi;
